import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Paper,
  Avatar,
  Chip,
} from "@mui/material";
import PageHeader from "../components/PageHeader";
import DownloadIcon from "@mui/icons-material/Download";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

// Journal issues data — update as new issues are published
const journalIssues = [
  {
    volume: 1,
    issue: 1,
    season: "Spring",
    year: 2025,
    pdfUrl: "/assets/journal/vol1-issue1.pdf",
  },
  {
    volume: 1,
    issue: 2,
    season: "Fall",
    year: 2025,
    pdfUrl: "/assets/journal/vol1-issue2.pdf",
  },
  {
    volume: 2,
    issue: 1,
    season: "Spring",
    year: 2026,
    pdfUrl: "/assets/journal/vol2-issue1.pdf",
  },
];

// Group issues by volume
const groupedByVolume = journalIssues.reduce((acc, issue) => {
  const key = `Volume ${issue.volume}`;
  if (!acc[key]) acc[key] = [];
  acc[key].push(issue);
  return acc;
}, {});

const JournalArchive = () => {
  return (
    <>
      <PageHeader
        title="Academic Journal"
        subtitle="Browse all published issues of the Academic Journal of American College."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Research", href: "/research" },
          { label: "Academic Journal", href: "/research/journal" },
        ]}
      />

      <Container maxWidth="lg" sx={{ mb: 10, mt: 4 }}>

        {/* Journal description banner */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 2,
            background: "linear-gradient(135deg, #1A237E 0%, #283593 100%)",
            color: "white",
            mb: 6,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.05)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -40,
              right: 100,
              width: 120,
              height: 120,
              borderRadius: "50%",
              background: "rgba(229,57,53,0.2)",
            }}
          />
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3, position: "relative" }}>
            <Avatar
              sx={{
                backgroundColor: "rgba(255,255,255,0.15)",
                width: 60,
                height: 60,
                flexShrink: 0,
              }}
            >
              <LibraryBooksIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, mb: 1.5, fontSize: { xs: "1.5rem", md: "2rem" } }}
              >
                Academic Journal of American College
              </Typography>
              <Typography
                variant="body1"
                sx={{ opacity: 0.9, lineHeight: 1.8, maxWidth: 680 }}
              >
                A biannual publication dedicated to sharing student research,
                institutional studies, academic essays, and scholarly
                contributions produced within the college community.
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
                <Chip
                  label="Biannual"
                  size="small"
                  sx={{ backgroundColor: "rgba(255,255,255,0.15)", color: "white" }}
                />
                <Chip
                  label="Student Research"
                  size="small"
                  sx={{ backgroundColor: "rgba(229,57,53,0.3)", color: "white" }}
                />
                <Chip
                  label="Open Access"
                  size="small"
                  sx={{ backgroundColor: "rgba(255,255,255,0.15)", color: "white" }}
                />
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Issues grouped by volume */}
        {Object.entries(groupedByVolume).map(([volumeLabel, issues]) => (
          <Box key={volumeLabel} sx={{ mb: 6 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 3,
              }}
            >
              <Box
                sx={{
                  width: 4,
                  height: 36,
                  borderRadius: 2,
                  backgroundColor: "#E53935",
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="h5"
                sx={{ color: "#1A237E", fontWeight: 700 }}
              >
                {volumeLabel}
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {issues.map((issue, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      border: "1px solid #E0E0E0",
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-6px)",
                        boxShadow: "0 12px 28px rgba(26,35,126,0.12)",
                        borderColor: "#1A237E",
                      },
                    }}
                  >
                    {/* Top color band */}
                    <Box
                      sx={{
                        height: 6,
                        background:
                          issue.season === "Spring"
                            ? "linear-gradient(90deg, #1A237E, #3949AB)"
                            : "linear-gradient(90deg, #E53935, #EF5350)",
                      }}
                    />
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <CalendarTodayIcon
                          sx={{ fontSize: 16, color: "#999" }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "#999", fontWeight: 500 }}
                        >
                          {issue.season} {issue.year}
                        </Typography>
                      </Box>

                      <Typography
                        variant="h6"
                        sx={{
                          color: "#1A237E",
                          fontWeight: 700,
                          mb: 0.5,
                        }}
                      >
                        Volume {issue.volume}, Issue {issue.issue}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ color: "#888", mb: 3 }}
                      >
                        {issue.season} {issue.year} Edition
                      </Typography>

                      <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        href={issue.pdfUrl}
                        download
                        fullWidth
                        sx={{
                          backgroundColor: "#1A237E",
                          color: "white",
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: 2,
                          py: 1,
                          "&:hover": { backgroundColor: "#0d1657" },
                        }}
                      >
                        Download PDF
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}

        {/* Back to Research link */}
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Button
            variant="outlined"
            href="/research"
            sx={{
              borderColor: "#1A237E",
              color: "#1A237E",
              textTransform: "none",
              px: 4,
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 600,
              borderRadius: 2,
              "&:hover": {
                borderColor: "#E53935",
                backgroundColor: "rgba(229,57,53,0.05)",
                color: "#E53935",
              },
            }}
          >
            ← Back to Research
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default JournalArchive;